import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Fab,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import * as React from "react";
import {
  CategoriesType,
  RepresentativeType,
  StudentFormType,
} from "../../types/entitiesModel";
import { ERROR_MESSAGE, OK_MESSAGE } from "../../utils/Constants";
import { CheckCircleOutline } from "@mui/icons-material";
import { messageType } from "../../types/otherTypes";
import { useNavigate, useParams } from "react-router-dom";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import { fetchRepresentatives, fetchCategories } from "../../utils/Utils";
import { AuthContext } from "../../context/AuthContext";


const EditStudent: React.FC = () => {
  const { token } = React.useContext(AuthContext) || {};
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [formState, setFormState] = React.useState<StudentFormType>({
    name: "",
    document: "",
    email: "",
    phone: "",
    address: "",
    representative_id: 0,
    category_id: 0,
    imageString: ""
  });

  const [categories, setCategories] = React.useState<CategoriesType[]>([]);
  const [representatives, setRepresentatives] = React.useState<
    RepresentativeType[]
  >([]);

  const [message, setMessage] = React.useState<messageType>({
    message: "",
    type: OK_MESSAGE, // can change
  });

  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);

  /**
   * To handle form data
   *
   * @param event
   */
  const handleChangeForm = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;

    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleBackView = () => {
    navigate("/admin/students");
  };

  /**
   * Send data to api, to save the category
   */
  const handleSubmit = async () => {
    const requestBody = {
      name: formState.name,
      document: formState.document,
      email: formState.email,
      phone: formState.phone,
      address: formState.email,
      representative_id: formState.representative_id,
      category_id: formState.category_id,
    };

    try {
      const response = await fetch(`/api/students/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        const responseData = await response.json();

        setMessage({
          message: `Success: ${
            responseData.messages || "Data enviada con éxito"
          }`,
          type: OK_MESSAGE,
        });

        setTimeout(() => {
          navigate("/admin/students");
        }, 1000);
      } else {
        const errorData = await response.json();
        setMessage({
          message: `Error: ${errorData.message || "Algo ha ido mal"}`,
          type: ERROR_MESSAGE,
        });
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        setMessage({
          message: `Error: ${
            error.message || "Ha ocurrido un error inesperado"
          }`,
          type: ERROR_MESSAGE,
        });
      } else {
        setMessage({
          message: "Error: Error desconocido",
          type: ERROR_MESSAGE,
        });
      }
    }
  };

  React.useEffect(() => {
    fetchCategories(setLoading, setCategories, setError);
    fetchRepresentatives(setLoading, setRepresentatives, setError);
  }, []);

  return (
    <React.Fragment>
      {!loading && categories.length > 0 && representatives.length > 0 ? (
        <>
          {error && (
            <Alert
              sx={{
                mx: "auto",
                my: 2,
                width: "95%",
              }}
              severity="error"
            >
              {error}
            </Alert>
          )}
          <Grid container spacing={0}>
            {message.message !== "" &&
              (message.type === OK_MESSAGE ? (
                <Alert
                  icon={<CheckCircleOutline fontSize="inherit" />}
                  severity="success"
                  sx={{
                    mx: "auto",
                    mb: 2,
                    width: "95%",
                  }}
                >
                  {message.message}
                </Alert>
              ) : (
                <Alert
                  sx={{
                    m: "auto",
                    mb: 2,
                    width: "95%",
                  }}
                  severity="error"
                >
                  {message.message}
                </Alert>
              ))}
            <Fab
              color="primary"
              variant="extended"
              sx={{
                mr: 1,
                mb: {
                  xs: 1,
                  sm: 0,
                  lg: 0,
                },
                ml: 2,
              }}
              onClick={handleBackView}
            >
              <KeyboardDoubleArrowLeftIcon />
              <Typography
                sx={{
                  ml: 1,
                  textTransform: "capitalize",
                }}
              >
                {"Tabla de estudiantes"}
              </Typography>
            </Fab>
            <Grid item lg={12} md={12} xs={12}>
              <div>
                <Card
                  variant="outlined"
                  sx={{
                    p: 0,
                  }}
                >
                  <Box
                    sx={{
                      padding: "15px 30px",
                    }}
                    display="flex"
                    alignItems="center"
                  >
                    <Box flexGrow={1}>
                      <Typography
                        sx={{
                          fontSize: "18px",
                          fontWeight: "500",
                        }}
                      >
                        Edición del estudiante {id}
                      </Typography>
                    </Box>
                  </Box>
                  <Divider />
                  <CardContent
                    sx={{
                      padding: "30px",
                    }}
                  >
                    <form>
                      {/* Nombre, Documento y representante */}
                      <Grid container spacing={2}>
                        <Grid item md={6} xs={12}>
                          <TextField
                            id="name"
                            label="Nombre"
                            variant="outlined"
                            placeholder="Nombre"
                            fullWidth
                            sx={{
                              mb: 2,
                            }}
                            name="name"
                            onChange={handleChangeForm}
                          />
                          <TextField
                            id="document"
                            label="Documento"
                            type="text"
                            variant="outlined"
                            placeholder="Documento"
                            fullWidth
                            sx={{
                              mb: 2,
                            }}
                            name="document"
                            onChange={handleChangeForm}
                          />
                          <TextField
                            fullWidth
                            id="representative_id"
                            variant="outlined"
                            select
                            label="Representante"
                            name="representative_id"
                            value={formState.representative_id}
                            onChange={handleChangeForm}
                            sx={{ mb: 2 }}
                          >
                            <MenuItem value={0}>
                              Selecciona un representante
                            </MenuItem>
                            {Array.isArray(representatives) &&
                            representatives.length > 0 ? (
                              representatives.map((representative) => (
                                <MenuItem
                                  key={representative.id}
                                  value={representative.id}
                                >
                                  {representative.name}
                                </MenuItem>
                              ))
                            ) : (
                              <MenuItem disabled value={0}>
                                No hay representantes disponibles
                              </MenuItem>
                            )}
                          </TextField>
                          <TextField
                            id="phone"
                            label="Teléfono"
                            type="text"
                            variant="outlined"
                            placeholder="Teléfono"
                            fullWidth
                            sx={{
                              mb: 2,
                            }}
                            name="phone"
                            onChange={handleChangeForm}
                          />
                        </Grid>
                        {/* Teléfono, Dirección y categoría */}
                        <Grid item md={6} xs={12}>
                          <TextField
                            id="email"
                            label="Email"
                            type="text"
                            variant="outlined"
                            placeholder="Email"
                            fullWidth
                            sx={{
                              mb: 2,
                            }}
                            name="email"
                            onChange={handleChangeForm}
                          />
                          <TextField
                            id="address"
                            label="Dirección"
                            type="text"
                            variant="outlined"
                            placeholder="Dirección"
                            fullWidth
                            sx={{
                              mb: 2,
                            }}
                            name="address"
                            onChange={handleChangeForm}
                          />
                          <TextField
                            fullWidth
                            id="category_id"
                            variant="outlined"
                            select
                            label="Categoría"
                            name="category_id"
                            value={formState.category_id}
                            onChange={handleChangeForm}
                            sx={{ mb: 2 }}
                          >
                            <MenuItem value={0}>
                              Selecciona una categoría
                            </MenuItem>
                            {Array.isArray(categories) &&
                            categories.length > 0 ? (
                              categories.map((category) => (
                                <MenuItem key={category.id} value={category.id}>
                                  {category.name}
                                </MenuItem>
                              ))
                            ) : (
                              <MenuItem disabled value={0}>
                                No hay categorías disponibles
                              </MenuItem>
                            )}
                          </TextField>
                        </Grid>
                      </Grid>
                      <Grid container>
                        <Button
                          onClick={() => {
                            handleSubmit();
                          }}
                          color="primary"
                          variant="contained"
                        >
                          Enviar
                        </Button>
                      </Grid>
                    </form>
                  </CardContent>
                </Card>
              </div>
            </Grid>
          </Grid>
        </>
      ) : (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100vh"
        >
          <CircularProgress />
        </Box>
      )}
    </React.Fragment>
  );
};

export default EditStudent;
